<template>
  <div class="container-fluid py-4 idp-page">
    <div class="row justify-content-center">
      <div class="card border-0 shadow-sm idp-card">
          <div class="card-body p-4 p-md-5">
            <div class="approval-hero mb-4">
              <div>
                <span class="approval-eyebrow">Approval Workspace</span>
                <h2 class="mb-1 font-weight-bold">IDP Approval Dashboard</h2>
                <p class="text-muted mb-0">Individual Development Plan - Approval by KTT</p>
              </div>
              <div class="approval-brand">
                <img
                  src="/img/logo-bib-color.png"
                  alt="BIB Academy"
                  class="approval-logo"
                />
                <span class="approval-brand-caption">Powered by UGEMS University</span>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading && employees.length === 0" class="alert alert-info mb-0 text-center">
              Loading data...
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger mb-0 text-center">
              {{ error }}
            </div>

            <!-- Data Table -->
            <div v-else>
              <!-- Legend Section -->
              <div class="legend-section">
                <div class="row">
                  <div class="col">
                    <span class="legend-key badge badge-warning mandatory-badge">M</span> = Mandatory
                  </div>
                  <div class="col">
                    <span class="legend-key badge badge-info">D</span> = Development
                  </div>
                </div>
              </div>

              <p class="text-muted mb-4">
                Scroll horizontally to view all training requirements. Select status for
        each employee-training combination.
              </p>

              <div class="matrix-toolbar">
                <div>
                  <h4 class="text-uppercase text-secondary mb-1">Training Approval Matrix</h4>
                  <small class="text-muted">Review peserta yang menunggu approval pada role ini.</small>
                </div>
                <span class="pending-pill">{{ pagination.totalItems }} pending item</span>
              </div>

              <div class="table-wrapper table-responsive border rounded">
                <table class="table table-bordered table-striped mb-0 approval-table">
                  <thead>
                    <tr>
                      <th class="align-middle text-center checkbox-col">
                        <input
                          type="checkbox"
                          v-model="selectAllCurrentPage"
                          :disabled="employees.length === 0"
                        />
                      </th>
                      <th class="align-middle text-center number-col">NO</th>
                      <th class="align-middle">NIK</th>
                      <th class="align-middle">EMPLOYEE NAME</th>
                      <th class="align-middle">JABATAN</th>
                      <th class="align-middle">COMPANY</th>
                      <th class="align-middle">TITLE</th>
                      <th class="align-middle">COURSE START/ END DATE</th>
                      <th class="align-middle">COURSE PRICE (IDR)</th>
                      <th class="align-middle">COURSE TYPE</th>
                      <th class="align-middle">LSP</th>
                      <th class="align-middle">LDP</th>
                      <th class="align-middle">REMARK</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="employees.length === 0">
                      <td :colspan="13" class="text-center text-muted py-4">
                        No data available
                      </td>
                    </tr>
                    <tr v-for="(row, index) in employees" :key="getRowKey(row)">
                      <td class="text-center">
                        <input
                          type="checkbox"
                          :checked="isRowSelected(row)"
                          @change="toggleRowSelection(row, $event.target.checked)"
                        />
                      </td>
                      <td class="text-center">{{ getRowNumber(index) }}</td>
                      <td>{{ row.nik || '-' }}</td>
                      <td>{{ row.name || '-' }}</td>
                      <td>{{ row.position || '-' }}</td>
                      <td>{{ row.company || '-' }}</td>
                      <td>{{ row.training_title || '-' }}</td>
                      <td>{{ formatCourseDateRange(row) }}</td>
                      <td>{{ formatCoursePrice(row.course_price) }}</td>
                      <td>{{ row.course_type || '-' }}</td>
                      <td>{{ row.lsp || '-' }}</td>
                      <td>{{ row.ldp || '-' }}</td>
                      <td>
                        <span :class="[getRemarkClass(row.training_status), 'remark-text']">
                          {{ row.training_status || '-' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="loading && employees.length > 0" class="table-loading-overlay">
                  <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Loading page...
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center flex-wrap mt-3 gap-2">
                <div class="d-flex align-items-center flex-wrap">
                  <small class="text-muted mr-2">Show</small>
                  <select
                    class="form-control form-control-sm per-page-select mr-2"
                    :value="pagination.limit"
                    :disabled="loading"
                    @change="onPerPageChange($event.target.value)"
                  >
                    <option v-for="option in perPageOptions" :key="option" :value="option">
                      {{ option }}
                    </option>
                  </select>
                  <small class="text-muted">
                    Showing {{ showingFrom }} - {{ showingTo }} of {{ pagination.totalItems }} | Dipilih: {{ selectedRows.length }}
                  </small>
                </div>
                <b-pagination
                  :value="pagination.page"
                  :total-rows="pagination.totalItems"
                  :per-page="pagination.limit"
                  :disabled="loading"
                  first-number
                  last-number
                  align="right"
                  size="sm"
                  @input="onPageChange"
                />
              </div>

              <div v-if="employees.length > 0" class="approval-actions mt-4 pt-3 border-top">
                <button class="btn btn-outline-danger" @click="handleReject" :disabled="selectedRows.length < 1 || actionLoading">
                  Reject Selected
                </button>
                <button class="btn btn-outline-success" @click="handleApproveAll" :disabled="pagination.totalItems < 1 || loading || actionLoading">
                  <span v-if="actionLoading && approveMode === 'all'" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Approve All
                </button>
                <button class="btn btn-success" @click="handleApprove" :disabled="selectedRows.length < 1 || actionLoading">
                  <span v-if="actionLoading && approveMode === 'selected'" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Approve Selected
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>

    <!-- Approve Confirmation Modal -->
    <div
      v-if="showApproveModal"
      class="modal-overlay"
      @click.self="closeApproveModal"
    >
      <div class="modal-content border-0 shadow">
        <div class="modal-header bg-light">
          <h4>Setujui Pengajuan IDP</h4>
          <button class="close-btn" @click="closeApproveModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="confirm-message">
            Apakah Anda yakin ingin menyetujui pengajuan IDP {{ approveMode === 'all' ? 'semua' : 'yang dipilih' }}?
          </p>
          <p class="confirm-detail">
            Tindakan ini akan menyetujui {{ approveTargetCount }} permintaan pelatihan yang masih pending.
          </p>
        </div>
        <div class="modal-footer bg-light">
          <button class="btn btn-secondary" @click="closeApproveModal" :disabled="actionLoading">
            Batal
          </button>
          <button class="btn btn-success" @click="confirmApprove" :disabled="actionLoading">
            <span v-if="actionLoading" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            {{ actionLoading ? 'Menyetujui...' : 'Konfirmasi Setujui' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reject Confirmation Modal -->
    <div
      v-if="showRejectModal"
      class="modal-overlay"
      @click.self="closeRejectModal"
    >
      <div class="modal-content border-0 shadow">
        <div class="modal-header bg-light">
          <h4>Tolak Pengajuan IDP</h4>
          <button class="close-btn" @click="closeRejectModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="confirm-message">
            Apakah Anda yakin ingin menolak pengajuan IDP yang dipilih?
          </p>
          <p class="confirm-detail">
            Tindakan ini hanya akan menolak permintaan pelatihan pending yang dipilih.
          </p>
        </div>
        <div class="modal-footer bg-light">
          <button class="btn btn-secondary" @click="closeRejectModal" :disabled="actionLoading">
            Batal
          </button>
          <button
            class="btn btn-danger"
            @click="confirmReject"
            :disabled="actionLoading"
          >
            <span v-if="actionLoading" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            {{ actionLoading ? 'Menolak...' : 'Konfirmasi Tolak' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      v-if="showSuccessModal"
      class="modal-overlay"
      @click.self="closeSuccessModal"
    >
      <div class="modal-content success-modal border-0 shadow">
        <div class="modal-header success-header bg-light">
          <div class="success-icon">✓</div>
          <h4>{{ successMessage.title }}</h4>
          <button class="close-btn" @click="closeSuccessModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="confirm-message">{{ successMessage.message }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-success" @click="closeSuccessModal">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useApprovalKTT } from "./main.logic";

export default {
  name: "IDPApprovalKTT",
  setup() {
    const {
      employees,
      pagination,
      loading,
      actionLoading,
      error,
      fetchEmployeeData,
      handleApproveAPI,
      handleRejectAPI,
      fetchAllEmployeeData,
    } = useApprovalKTT();

    const documentDate = new Date().toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const selectedRows = ref([]);
    const perPageOptions = [10, 20, 24, 50, 100];
    const showingFrom = computed(() => {
      if (pagination.value.totalItems === 0) return 0;
      return ((pagination.value.page - 1) * pagination.value.limit) + 1;
    });
    const showingTo = computed(() => {
      if (pagination.value.totalItems === 0) return 0;
      return Math.min(pagination.value.page * pagination.value.limit, pagination.value.totalItems);
    });
    const selectAllCurrentPage = computed({
      get() {
        return employees.value.length > 0 && employees.value.every((row) => isRowSelected(row));
      },
      set(checked) {
        if (checked) {
          employees.value.forEach((row) => {
            if (!isRowSelected(row)) {
              selectedRows.value.push(row);
            }
          });
          return;
        }

        const currentPageKeys = new Set(employees.value.map((row) => getRowKey(row)));
        selectedRows.value = selectedRows.value.filter((row) => !currentPageKeys.has(getRowKey(row)));
      },
    });

    const showApproveModal = ref(false);
    const showRejectModal = ref(false);
    const showSuccessModal = ref(false);
    const approveMode = ref("selected");
    const successMessage = ref({
      title: "",
      message: "",
    });

    const approveTargetCount = computed(() => {
      return approveMode.value === "all"
        ? pagination.value.totalItems
        : selectedRows.value.length;
    });

    // Get status class based on status value
    function getRemarkClass(remark) {
      if (!remark) return "";
      if (remark === "M") return "badge badge-warning mandatory-badge";
      if (remark === "D") return "badge badge-info";
      return "badge badge-secondary";
    }

    function getRowKey(row) {
      return row.idp_id || row.id;
    }

    function getRowNumber(index) {
      return ((pagination.value.page - 1) * pagination.value.limit) + index + 1;
    }

    function isRowSelected(row) {
      const key = getRowKey(row);
      return selectedRows.value.some((item) => getRowKey(item) === key);
    }

    function toggleRowSelection(row, checked) {
      const key = getRowKey(row);

      if (checked) {
        if (!isRowSelected(row)) {
          selectedRows.value.push(row);
        }
        return;
      }

      selectedRows.value = selectedRows.value.filter((item) => getRowKey(item) !== key);
    }

    function formatCourseDateRange(row) {
      const startDate = row?.start_date || "-";
      const endDate = row?.end_date || "-";
      return `${startDate} / ${endDate}`;
    }

    function formatCoursePrice(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return value;
      }

      return numberValue.toLocaleString("id-ID");
    }

    function getSelectedIdsPayload() {
      return selectedRows.value.map((row) => row.idp_id);
    }

    async function onPageChange(nextPage) {
      if (!nextPage || nextPage === pagination.value.page) {
        return;
      }
      await fetchEmployeeData({
        page: nextPage,
        limit: pagination.value.limit,
      });
    }

    async function onPerPageChange(nextLimit) {
      const parsedLimit = Number(nextLimit) || pagination.value.limit;
      await fetchEmployeeData({
        page: 1,
        limit: parsedLimit,
      });
    }

    // Handle approve button click - show modal
    function handleApprove() {
      approveMode.value = "selected";
      showApproveModal.value = true;
    }

    function handleApproveAll() {
      approveMode.value = "all";
      showApproveModal.value = true;
    }

    // Close approve modal
    function closeApproveModal() {
      if (actionLoading.value) return;
      showApproveModal.value = false;
    }

    // Confirm approve
    async function confirmApprove() {
      if (actionLoading.value) return;
      let selectedIds = getSelectedIdsPayload();
      try {
        actionLoading.value = true;
        if (approveMode.value === "all") {
          const allRows = await fetchAllEmployeeData();
          selectedIds = allRows.map((row) => row.idp_id);
        }
      } catch (error) {
        actionLoading.value = false;
        return;
      }
      const success = await handleApproveAPI(selectedIds);
      showApproveModal.value = false;
      if (success) {
        selectedRows.value = [];
      }
    }

    // Handle reject button click - show modal
    function handleReject() {
      showRejectModal.value = true;
    }

    // Close reject modal
    function closeRejectModal() {
      if (actionLoading.value) return;
      showRejectModal.value = false;
    }

    // Confirm reject
    async function confirmReject() {
      if (actionLoading.value) return;
      const selectedIds = getSelectedIdsPayload();
      const success = await handleRejectAPI('', selectedIds);
      showRejectModal.value = false;
      if (success) {
        selectedRows.value = [];
      }
    }

    // Close success modal
    function closeSuccessModal() {
      showSuccessModal.value = false;
    }

    return {
      employees,
      loading,
      actionLoading,
      error,
      documentDate,
      getRemarkClass,
      formatCourseDateRange,
      formatCoursePrice,
      showingFrom,
      showingTo,
      selectAllCurrentPage,
      pagination,
      selectedRows,
      perPageOptions,
      getRowKey,
      getRowNumber,
      isRowSelected,
      toggleRowSelection,
      onPageChange,
      onPerPageChange,
      showApproveModal,
      showRejectModal,
      showSuccessModal,
      approveMode,
      approveTargetCount,
      successMessage,
      handleApprove,
      handleApproveAll,
      closeApproveModal,
      confirmApprove,
      handleReject,
      closeRejectModal,
      confirmReject,
      closeSuccessModal,
    };
  },
};
</script>

<style scoped>
.idp-page {
  /* background: linear-gradient(180deg, #f8f9fb 0%, #f2f4f7 100%); */
  min-height: 100vh;
}

.idp-card {
  width: 90%;
}

.approval-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 22px 24px;
  border: 1px solid #f1d2d2;
  border-radius: 8px;
  background: #fffafa;
}

.approval-eyebrow {
  display: block;
  color: #b71c1c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.approval-brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  min-width: 190px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #f1d2d2;
  box-shadow: 0 8px 20px rgba(183, 28, 28, 0.08);
}

.approval-logo {
  width: 170px;
  max-width: 42vw;
  height: auto;
  object-fit: contain;
}

.approval-brand-caption {
  color: #8fa1c3;
  font-size: 11px;
  font-weight: 600;
}

.matrix-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
}

.pending-pill {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 7px 12px;
  border-radius: 999px;
  background: #fff1f1;
  color: #b71c1c;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.approval-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.table-wrapper {
  position: relative;
  overflow-x: visible;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: #b71c1c #f1f3f5;
}

.table-wrapper::-webkit-scrollbar {
  height: 12px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 10px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #b71c1c;
  border-radius: 10px;
  border: 2px solid #f1f3f5;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #8f1515;
}

.approval-table {
  min-width: max-content;
}

.approval-table thead th {
  background: #f8f9fa;
  color: #495057;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
}

.approval-table tbody td {
  vertical-align: middle;
  font-size: 13px;
}

.approval-table .checkbox-col {
  width: 48px;
}

.approval-table .number-col {
  width: 64px;
}

.approval-table input[type="checkbox"] {
  accent-color: #B71C1C;
}

.table-loading-overlay {
  position: absolute;
  top: 10px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #b71c1c;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
  z-index: 2;
}

.per-page-select {
  width: 76px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
}
.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal-header h4 {
  margin: 0;
  font-size: 18px;
  color: #333;
}
.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #888;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}
.close-btn:hover {
  background: #f0f0f0;
  color: #333;
}
.modal-body {
  padding: 24px;
}
.modal-body label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}
.required {
  color: #c62828;
}
.confirm-message {
  font-size: 16px;
  color: #333;
  margin: 0 0 12px 0;
  font-weight: 500;
}
.confirm-detail {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}
.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
}

.modal-footer .btn + .btn {
  margin-left: 8px;
}

.mandatory-badge {
  background: #f59e0b;
}

.remark-text {
  font-size: 13px;
}

/* Legend Section */
.legend-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.legend-section .row {
  font-size: 0.875rem;
}

.legend-key {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-weight: 700;
  font-size: 16px;
  border-radius: 3px;
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .approval-hero,
  .matrix-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .approval-brand {
    align-items: flex-start;
  }
}
</style>
